import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, sending, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    }
  };

  return (
    <div className="forgot-password-page" data-testid="forgot-password-page">
      <div className="forgot-container">
        <motion.div
          className="forgot-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link to="/" className="back-link">
            <ArrowLeft size={18} /> Back to Home
          </Link>

          {status === 'success' ? (
            <div className="success-state">
              <div className="success-icon">
                <CheckCircle size={48} />
              </div>
              <h1>Check Your Email</h1>
              <p>
                We've sent a password reset link to <strong>{email}</strong>. 
                Please check your inbox and follow the instructions to reset your password.
              </p>
              <p className="note">
                Didn't receive the email? Check your spam folder or 
                <button onClick={() => setStatus('idle')} className="retry-link">try again</button>
              </p>
            </div>
          ) : (
            <>
              <div className="forgot-header">
                <div className="forgot-icon">
                  <Mail size={32} />
                </div>
                <h1>Forgot Password?</h1>
                <p>No worries! Enter your email and we'll send you a reset link.</p>
              </div>

              <form onSubmit={handleSubmit} className="forgot-form" data-testid="forgot-password-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                {status === 'error' && (
                  <div className="error-message" data-testid="forgot-error">
                    <AlertCircle size={18} />
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={status === 'sending'}
                  data-testid="forgot-submit-btn"
                >
                  {status === 'sending' ? 'Sending...' : (
                    <>
                      <Send size={18} /> Send Reset Link
                    </>
                  )}
                </button>
              </form>

              <div className="forgot-footer">
                <p>Remember your password? <Link to="/">Sign in</Link></p>
              </div>
            </>
          )}
        </motion.div>
      </div>

      <style>{`
        .forgot-password-page {
          min-height: calc(100vh - 200px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
        }

        .forgot-container {
          width: 100%;
          max-width: 440px;
        }

        .forgot-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: var(--text-muted);
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 24px;
        }

        .back-link:hover {
          color: var(--text-primary);
        }

        .forgot-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .forgot-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .forgot-header h1 {
          font-size: 1.75rem;
          margin-bottom: 8px;
        }

        .forgot-header p {
          color: var(--text-muted);
          font-size: 14px;
        }

        .forgot-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .form-group input {
          padding: 12px 16px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 16px;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: var(--error-bg);
          color: var(--error);
          border-radius: 8px;
          font-size: 14px;
        }

        .btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--primary);
          color: #0a0a0f;
          border: none;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--primary-light);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .forgot-footer {
          text-align: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--border);
        }

        .forgot-footer p {
          color: var(--text-muted);
          font-size: 14px;
        }

        .forgot-footer a {
          color: var(--primary);
          text-decoration: none;
          font-weight: 500;
        }

        .forgot-footer a:hover {
          text-decoration: underline;
        }

        /* Success State */
        .success-state {
          text-align: center;
          padding: 20px 0;
        }

        .success-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(0, 255, 148, 0.1);
          color: var(--success);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .success-state h1 {
          font-size: 1.75rem;
          margin-bottom: 16px;
        }

        .success-state p {
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .success-state strong {
          color: var(--primary);
        }

        .note {
          font-size: 14px !important;
        }

        .retry-link {
          background: none;
          border: none;
          color: var(--primary);
          cursor: pointer;
          font-size: 14px;
          margin-left: 4px;
        }

        .retry-link:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
